/*
 *  Copyright © Microsoft Corporation. All rights reserved.
 */

import { IButtonStyles } from '@fluentui/react/lib/Button';
import { ICalloutContentStyles } from '@fluentui/react/lib/Callout';
import { ILabelStyles } from '@fluentui/react/lib/Label';
import { IStackStyles } from '@fluentui/react/lib/Stack';
import { FontWeights, ITheme } from '@fluentui/react/lib/Styling';
import { ITeachingBubbleStyles } from '@fluentui/react/lib/TeachingBubble';
import { ITooltipHostStyles } from '@fluentui/react/lib/Tooltip';

import { Theme } from '@msteams/verticals-ui-styles/Theme';
import { ContrastColors, FontSizes } from '../../../../../../styles/teams';
import { getAppHost } from '../../../../../../utilities/TeamsUtils';

export const getStackStyles = (theme: ITheme): Partial<IStackStyles> => {
    return {
        root: {
            backgroundColor: theme.palette.neutralLight,
            height: '4em',
            padding: '0 1.25em',
        },
    };
};

export const getLabelStyles = (theme: ITheme): Partial<ILabelStyles> => {
    return {
        root: {
            color: theme.palette.black,
            fontSize: FontSizes.size18,
            fontWeight: FontWeights.bold,
            padding: '0 0.875em',
        },
    };
};

export const getMoreButtonStyles = (theme: ITheme): Partial<IButtonStyles> => {
    return {
        menuIcon: { display: 'none' },
        iconHovered: { color: theme.palette.themePrimary },
        rootHovered: {
            background: 'transparent',
        },
    };
};

export const getCalloutStyles = (theme: ITheme): Partial<ICalloutContentStyles> => {
    const themeName = getAppHost().getTheme();
    let itemHoverColor = theme.palette.neutralDark;
    if (themeName === Theme.Contrast) {
        itemHoverColor = ContrastColors.appWhite;
    }

    return {
        root: {
            marginTop: '0.75rem',
            selectors: {
                '.ms-ContextualMenu-link:hover, .ms-ContextualMenu-link:focus, .ms-ContextualMenu-link:focus:hover': {
                    backgroundColor: theme.palette.themeLight,
                    color: itemHoverColor,
                    selectors: {
                        '.ms-ContextualMenu-icon': {
                            color: itemHoverColor,
                        },
                    },
                },
            },
            borderWidth: 0,
        },
    };
};

export const getTooltipHostStyles = (): Partial<ITooltipHostStyles> => {
    return {
        root: { display: 'inline-block' },
    };
};

export const getTeachingBubbleStyles = (): Partial<ITeachingBubbleStyles> => {
    return {
        header: { marginBottom: '0.75rem !important' },
        headline: { fontSize: FontSizes.size18 },
        subText: { fontSize: FontSizes.size12 },
        closeButton: {
            margin: '5px 15px 0px 0px',
            marginInlineEnd: '0px',
        },
        bodyContent: { padding: '0.5rem 1rem', borderRadius: '6px' },
        footer: {
            selectors: {
                '&>div:last-of-type': {
                    width: '100%',
                },
                button: {
                    width: '100%',
                    borderRadius: '4px',
                    marginBottom: '0.25rem',
                },
            },
        },
    };
};

export const getOuterPromotionTeachingBubbleStyles = (): Partial<ITeachingBubbleStyles> => {
    return {
        header: { marginBottom: '0.75rem !important', marginRight: '2.5rem' },
        headline: { fontSize: FontSizes.size18 },
        subText: { fontSize: FontSizes.size12 },
        bodyContent: { padding: '0.5rem 1rem', borderRadius: '4px' },
        footer: {
            selectors: {
                '&>div:last-of-type': {
                    width: '100%',
                },
                button: {
                    width: '100%',
                    borderRadius: '4px',
                    marginBottom: '0.25rem',
                },
            },
        },
        closeButton: {
            marginTop: '10px',
        },
    };
};

export const getInnerPromotionTeachingBubbleStyles = (): Partial<ITeachingBubbleStyles> => {
    return {
        body: {
            selectors: {
                ':not(:last-child)': {
                    marginBottom: 0,
                },
            },
        },
        bodyContent: { padding: '0.5rem 0.75rem', fontSize: FontSizes.size12 },
        closeButton: {
            margin: '1px 0 0 0',
        },
    };
};
